// Maryland HCBS Programs
export type Program = 'CO' | 'CFC' | 'CPAS' | 'EPSDT' | 'REM';

// Programs where overnight shifts are expected (Private Duty Nursing)
export const OVERNIGHT_ALLOWED_PROGRAMS: Program[] = ['REM', 'EPSDT'];

// Service status from LTSS
export type ServiceStatus = 
  | 'Closed' 
  | 'New: Missing Time' 
  | 'Ready' 
  | 'Pending MDH' 
  | 'Not Authorized';

// Claim history values
export type ClaimHistory = 
  | 'Original Claim Paid'
  | 'Original Claim NotSubmitted'
  | 'Original Claim Submitted'
  | 'Void Claim Paid'
  | 'Adjustment Claim Paid'
  | 'Adjustment In Progress';

// Main service record from LTSS report
export interface LTSSService {
  // Client identification
  clientName: string;
  clientId: string;
  clientMA: string;
  
  // Provider identification
  providerName: string;
  providerNumber: string;
  
  // Service details
  week: string; // e.g., "09/25/2025 - 10/01/2025"
  serviceType: string;
  temporaryService: boolean;
  emergencyService: boolean;
  program: Program;
  
  // Authorization
  weeklyPosHours: number;
  posHours: number;
  
  // Time tracking
  dayOfService: string;
  serviceDate: Date | null;
  startTime: string | null;
  endTime: string | null;
  serviceLength: number | null;
  totalServiceLength: number | null;
  
  // Staff information
  staffName: string;
  staffId: string;
  staffLoginId: string;
  
  // EVV compliance
  serviceInitiationSource: string;
  
  // Billing
  procedureCode: string;
  serviceStatus: ServiceStatus;
  totalServiceLengthUnits: number | null;
  unitsBilled: number | null;
  claimHistory: ClaimHistory | null;
  amountPaid: number | null;
  
  // Adjustments
  pendingAdjustmentStartTime: string | null;
  pendingAdjustmentEndTime: string | null;
  pendingAdjustmentDuration: number | null;
  pendingAdjustmentUnits: number | null;
  pendingAdjustmentStatus: string | null;
  
  // Comments
  serviceComments: string | null;
  claimComments: string | null;
  
  // Location (GPS)
  evvAppClockInLocation: string | null;
  gpsClockInLocation: string | null;
  evvAppClockOutLocation: string | null;
  gpsClockOutLocation: string | null;
}

// Alert severity levels
export type AlertLevel = 'CRITICAL' | 'WARNING' | 'CAUTION' | 'INFO';

// Alert structure
export interface Alert {
  id: string;
  type: AlertLevel;
  category: string;
  message: string;
  details: string;
  relatedService?: LTSSService;
  staffName?: string;
  clientName?: string;
  timestamp?: Date;
}

// Weekly hours for payroll
export interface WeeklyHours {
  weekStart: Date;
  weekEnd: Date;
  weekLabel: string;
  hours: number; // Actual hours worked
  billableHours: number; // Hours capped by program daily limits
  services: number;
}

// Staff metrics
export interface StaffMetrics {
  staffName: string;
  staffId: string;
  totalServices: number;
  totalHours: number; // Actual hours worked
  billableHours: number; // Hours capped by program daily limits
  weeklyHours: WeeklyHours[]; // Hours breakdown by week for payroll
  manualCorrections: number;
  manualCorrectionDates: Date[]; // Dates when manual corrections occurred
  missingTimeIncidents: number;
  cleanClockIns: number;
  cleanRate: number;
  penaltyStatus: 'ok' | 'risk' | 'exceeded';
}

// Client utilization
export interface ClientUtilization {
  clientName: string;
  clientId: string;
  program: Program;
  week: string;
  authorizedHours: number;
  usedHours: number;
  variance: number; // negative = underutilized, positive = over authorized
  utilizationRate: number;
  revenue: number;
  serviceCount: number;
  missedRevenue: number; // Revenue lost from unused authorized hours
}

// Financial summary
export interface FinancialSummary {
  totalUnitsBilled: number;
  totalAmountPaid: number;
  claimsPaid: number;
  claimsPending: number;
  claimsNotSubmitted: number;
  claimsVoided: number;
  estimatedLeakage: number;
  totalMissedRevenue: number; // Revenue lost from underutilized authorized hours
}
